import * as React from 'react';
import { dataSource } from '@/data';
import { ehAcessoNegado, mensagemDoErro } from '@/data/http';
import type { IncidenteResumo } from '@/data/types';
import { avisoDeHomologacao } from '@/lib/incidentes';
import { AvisoHomologacaoDialog, type GrupoAviso, type PedidoAviso } from './AvisoHomologacaoDialog';

/**
 * Aviso ao homologar uma versão com incidente aberto/mitigado.
 *
 * NÃO bloqueia (a API deixa passar): lista os incidentes (severidade e título) e
 * exige "Homologar mesmo assim". Alta/crítica ganha destaque. Vale no detalhe,
 * na fila e no lote — o lote mostra um diálogo só, agrupado por versão.
 *
 * A regra em si (`avisoDeHomologacao`) é pura e testada em `lib/incidentes.ts`.
 */

export interface AlvoHomologacao {
  versao: string;
  produto: string;
  /** Se já se conhece a lista (detalhe da versão), evita a consulta. */
  incidentes?: IncidenteResumo[];
}

export function useAvisoHomologacao(): {
  /** Resolve `true` se pode homologar (sem incidente, ou confirmado) e `false` se a pessoa desistiu. */
  confirmar: (alvos: AlvoHomologacao[]) => Promise<boolean>;
  dialogo: React.ReactNode;
} {
  const [pedido, setPedido] = React.useState<PedidoAviso | null>(null);

  const confirmar = React.useCallback(async (alvos: AlvoHomologacao[]): Promise<boolean> => {
    const grupos = await Promise.all(
      alvos.map(async (alvo): Promise<GrupoAviso> => {
        try {
          const incidentes = alvo.incidentes ?? (await dataSource.listarIncidentesDaVersao(alvo.versao, alvo.produto));
          return { versao: alvo.versao, aviso: avisoDeHomologacao(incidentes), erro: null };
        } catch (causa) {
          // 403: sem acesso a incidentes não há o que avisar (e nada de erro vermelho).
          if (ehAcessoNegado(causa)) {
            return { versao: alvo.versao, aviso: avisoDeHomologacao([]), erro: null };
          }
          return {
            versao: alvo.versao,
            aviso: { naoResolvidos: [], grave: false, exigeConfirmacao: true },
            erro: mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa)),
          };
        }
      }),
    );
    const comAviso = grupos.filter((grupo) => grupo.aviso.exigeConfirmacao);
    if (comAviso.length === 0) return true;
    return new Promise<boolean>((resolver) => setPedido({ grupos: comAviso, resolver }));
  }, []);

  function decidir(confirmado: boolean) {
    pedido?.resolver(confirmado);
    setPedido(null);
  }

  const dialogo = <AvisoHomologacaoDialog pedido={pedido} aoDecidir={decidir} />;
  return { confirmar, dialogo };
}

